import useSelectionStore from '@store/useSelectionStore';
import { ClickableType } from '@utils/enums';
import SpawnBeaconIcon from '@gameobjects/SpawnBeaconIcon';
import { Shield, Swords } from 'lucide-react';
import './StructureInfoPanel.css';

export default function StructureInfoPanel() {
  const { currentClick } = useSelectionStore();

  // Only show if a structure is selected
  if (!currentClick || currentClick.type !== ClickableType.STRUCTURE) {
    return null;
  }

  const structure = currentClick.data;

  if (!structure) {
    throw new Error('[StructureInfoPanel] Structure data missing');
  }

  // Validate required structure data
  if (!structure.teamClass) {
    throw new Error(`[StructureInfoPanel] Structure missing teamClass`);
  }

  if (!structure.subtype) {
    throw new Error(`[StructureInfoPanel] Structure missing subtype`);
  }

  // Check for HP (structures should have HP)
  if (structure.hp === undefined) {
    console.warn(`[StructureInfoPanel] Structure ${structure.id} missing hp value`);
  }

  // Handle different structure subtypes
  switch (structure.subtype) {
    case 'spawn_beacon':
      return <SpawnBeaconPanel structure={structure} />;
    default:
      console.warn(`[StructureInfoPanel] Unknown structure subtype: ${structure.subtype}`);
      return (
        <div className="structure-info-card">
          <h3>Unknown Structure: {structure.subtype}</h3>
          <p>Position: ({structure.x}, {structure.y})</p>
        </div>
      );
  }
}

// Spawn Beacon specific panel
function SpawnBeaconPanel({ structure }) {
  // For capture mechanics (if structure has them)
  const hasCapture = structure.capture_required !== undefined;
  const capturePercentage = hasCapture && structure.capture_required > 0
    ? (structure.capture_points / structure.capture_required) * 100
    : 0;

  return (
    <div className="structure-info-card">
      <div className="structure-header">
        <SpawnBeaconIcon beacon={structure} size="big" />
        <h3 className="structure-title">Spawn Beacon</h3>
      </div>

      {/* Control Status */}
      <div className={`structure-control team-${structure.teamClass}`}>
        <Shield size={16} />
        <span>Controlled by {structure.teamClass === 'own' ? 'You' : structure.teamClass}</span>
      </div>

      {/* Capture Progress (if applicable) */}
      {hasCapture && structure.capturing_team_class && structure.capturing_team_class !== structure.teamClass && (
        <div className="structure-capture">
          <div className={`capturing-team team-${structure.capturing_team_class}`}>
            <Swords size={16} />
            <span>{structure.capturing_team_class === 'own' ? 'You are' : `${structure.capturing_team_class} is`} capturing!</span>
          </div>

          <div className="capture-progress">
            <div className="progress-bar">
              <div
                className={`progress-fill team-${structure.capturing_team_class}`}
                style={{ width: `${capturePercentage}%` }}
              />
            </div>
            <span className="progress-text">
              {structure.capture_points} / {structure.capture_required}
            </span>
          </div>
        </div>
      )}

      {/* Position */}
      <div className="structure-position">
        <span>Position: ({structure.x}, {structure.y})</span>
      </div>

      {/* Capture requirement if exists */}
      {hasCapture && (
        <div className="structure-stats">
          <span>Capture Required: {structure.capture_required} points</span>
        </div>
      )}
    </div>
  );
}